import React, { useState, forwardRef, useRef, useImperativeHandle, useContext, useEffect } from "react";
import {
  CommandBar,
  ICommandBarItemProps,
  Dialog,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  DialogType,
  IModalProps
} from "@fluentui/react";
import { v4 } from "uuid";
import CustomButton from "../CustomButton";
import NotificationMessages, { INotificationMessagesRef } from "../NotificationMessages";
import { SLLoadingStatus, SLActionStatus } from "../../utilities/utilities";
import { SLCreateSLBatchContext } from "../../contexts/SLCreateSLBatchContext";

export type AllSLCreateActions =
  | "save"
  | "post"
  | "sendToPoster"
  | "validate"
  | "approve"
  | "needsClarification"
  | "recall"
  | "clearForm";

export interface IFormProps {
  onCommand: (commandName: AllSLCreateActions, userText?: string) => void;
  children: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SLCreateActionBarRef extends INotificationMessagesRef {}

const SLCreateActionBar: React.ForwardRefExoticComponent<IFormProps & React.RefAttributes<SLCreateActionBarRef>> =
  forwardRef((props, ref) => {
    const slCreateContext = useContext(SLCreateSLBatchContext);
    const [hideDialogClear, setHideDialogClear] = useState(true);
    const [hideDialogPostToSap, setHideDialogPostToSap] = useState(true);

    const [actions, setActions] = useState<ICommandBarItemProps[]>(() => {
      return [
        {
          key: "post",
          name: "Post",
          iconProps: { iconName: "Mail" },
          disabled: true
        },
        {
          key: "save",
          name: "Save",
          iconProps: { iconName: "Save" },
          disabled: true
        },
        {
          key: "sendToPoster",
          name: "Send to poster",
          iconProps: { iconName: "MailForward" },
          disabled: true
        },
        {
          key: "approve",
          name: "Approve",
          iconProps: { iconName: "DocumentApproval" },
          disabled: true
        },
        {
          key: "needsClarification",
          name: "Needs Clarification",
          iconProps: { iconName: "SurveyQuestions" },
          disabled: true
        },
        {
          key: "recall",
          name: "Recall",
          iconProps: { iconName: "Undo" },
          disabled: true
        },
        {
          key: "validate",
          name: "Validate",
          iconProps: { iconName: "ReceiptCheck" },
          disabled: true
        },
        {
          key: "clearForm",
          name: "Clear Form",
          iconProps: { iconName: "ClearFilter" },
          disabled: true
        }
      ];
    });
    const notificationsRef = useRef<INotificationMessagesRef>(null);

    const _onCommand = (commandName: AllSLCreateActions, userText?: string) => {
      if (slCreateContext.hasErrors()) {
        notificationsRef.current?.reset();
        notificationsRef.current?.setMessage("errors", [
          "Please enter data for all the required fields before performing the action."
        ]);
      } else {
        props.onCommand(commandName, userText);
      }
    };

    useImperativeHandle(ref, () => ({
      setMessage(type, messages) {
        if (notificationsRef.current) {
          notificationsRef.current.reset();
          notificationsRef.current.setMessage(type, messages);
        }
      },
      reset() {
        if (notificationsRef.current) {
          notificationsRef.current.reset();
        }
      }
    }));

    useEffect(() => {
      if (slCreateContext.loadingStatus === SLLoadingStatus.Resolved) {
        setActions([
          {
            key: "save",
            name: "Save",
            iconProps: { iconName: "Save" },
            onClick: () => _onCommand("save"),
            disabled: slCreateContext.getPermission("save") === SLActionStatus.Disabled
          },
          {
            key: "post",
            name: "Post",
            iconProps: { iconName: "Mail" },
            onClick: () => setHideDialogPostToSap(false),
            disabled: slCreateContext.getPermission("post") === SLActionStatus.Disabled
          },
          {
            key: "sendToPoster",
            name: "Send to poster",
            iconProps: { iconName: "MailForward" },
            onClick: () => _onCommand("sendToPoster"),
            disabled: slCreateContext.getPermission("sendToPoster") === SLActionStatus.Disabled
          },
          {
            key: "approve",
            name: "Approve",
            iconProps: { iconName: "DocumentApproval" },
            onClick: () => _onCommand("approve"),
            disabled: slCreateContext.getPermission("approve") === SLActionStatus.Disabled
          },
          {
            key: "needsClarification",
            name: "Needs Clarification",
            iconProps: { iconName: "SurveyQuestions" },
            onClick: () => _onCommand("needsClarification"),
            disabled: slCreateContext.getPermission("needsClarification") === SLActionStatus.Disabled
          },
          {
            key: "recall",
            name: "Recall",
            iconProps: { iconName: "Undo" },
            onClick: () => _onCommand("recall"),
            disabled: slCreateContext.getPermission("recall") === SLActionStatus.Disabled
          },
          {
            key: "validate",
            name: "Validate",
            iconProps: { iconName: "ReceiptCheck" },
            onClick: () => _onCommand("validate"),
            disabled: slCreateContext.getPermission("validate") === SLActionStatus.Disabled
          },
          {
            key: "clearForm",
            name: "Clear Form",
            iconProps: { iconName: "ClearFilter" },
            onClick: () => setHideDialogClear(false),
            disabled: slCreateContext.getPermission("clearForm") === SLActionStatus.Disabled
          }
        ]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slCreateContext.loadingStatus]);

    const closeClearDialog = () => {
      setHideDialogClear(true);
    };
    const closePostToSapDialog = () => {
      setHideDialogPostToSap(true);
    };

    const clearStoreAction = async (ev: any) => {
      ev.preventDefault();
      props.onCommand("clearForm");
      setHideDialogClear(true);
    };

    const postToSapAction = async (ev: any) => {
      ev.preventDefault();
      _onCommand("post");
      setHideDialogPostToSap(true);
    };

    const dialogModalProps = {
      isBlocking: true,
      styles: { main: { maxWidth: 450 } }
    } as IModalProps;

    const id = v4();

    return (
      <React.Fragment>
        <form id={id} action="#" aria-label="Subledger entry actions">
          <NotificationMessages ref={notificationsRef} />
          <Dialog
            hidden={hideDialogPostToSap}
            onDismiss={closePostToSapDialog}
            dialogContentProps={{
              type: DialogType.normal,
              title: "Post to SAP",
              subText: "Would you like to save data and post to SAP?"
            }}
            modalProps={dialogModalProps}
          >
            <DialogFooter>
              <PrimaryButton onClick={postToSapAction} text="Post to SAP" />
              <DefaultButton onClick={closePostToSapDialog} text="Cancel" />
            </DialogFooter>
          </Dialog>
          <Dialog
            hidden={hideDialogClear}
            onDismiss={closeClearDialog}
            dialogContentProps={{
              type: DialogType.normal,
              title: "Clear Form",
              subText: "Would you like to reset this form?"
            }}
            modalProps={dialogModalProps}
          >
            <DialogFooter>
              <PrimaryButton onClick={clearStoreAction} text="Clear Form" />
              <DefaultButton onClick={closeClearDialog} text="Cancel" />
            </DialogFooter>
          </Dialog>
          <CommandBar
            buttonAs={CustomButton}
            items={actions}
            ariaLabel={"Actions for SLEntry"}
            overflowButtonProps={{ ariaLabel: "More Actions" }}
          />
          {props.children}
        </form>
      </React.Fragment>
    );
  });

export default SLCreateActionBar;
